import { Component, ErrorInfo, ReactNode } from 'react';

import { isChunkLoadError } from '@/shared/lib/chunkLoadError';

import AppCrashPage, { goToAppRoot } from './AppCrashPage';

export interface ErrorBoundaryFallbackProps {
  error: unknown;
  reset: () => void;
}

interface Props {
  children: ReactNode;
  renderFallback?: (props: ErrorBoundaryFallbackProps) => ReactNode;
}

interface State {
  hasError: boolean;
  error: unknown;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.group('🔴 ErrorBoundary caught');
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
  }

  private resetError = () => {
    // A failed chunk load cannot be retried in place: React.lazy caches the
    // rejected import, so resetting the boundary re-throws immediately. Only
    // a reload can fetch the current build.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.renderFallback) {
      return this.props.renderFallback({ error: this.state.error, reset: this.resetError });
    }

    return (
      <AppCrashPage
        error={this.state.error}
        fullScreen
        onRetry={this.resetError}
        onGoHome={goToAppRoot}
      />
    );
  }
}

export default ErrorBoundary;
